import { DataProvider, TypeHelper } from "../../helpers";
import { Enums } from "../common";
import { OpDataList } from "../util";
import { ICopyable, IJsonSerializable, IOpChangeState, IOpData, IOpObject, IReferenceType } from "../util/interfaces";
import { Utils } from "../util/utils";
import { OpDeviceOrderNumberData } from "./op-device-order-number-data";

export class OpDeviceOrderNumber
    implements
    ICopyable<OpDeviceOrderNumber>,
    IJsonSerializable,
    IOpObject,
    IOpChangeState,
    IReferenceType {

    //#region Properties

    public idDeviceOrderNumber: number;
    public name: string;
    public firstQuarter: string;
    public secondQuarter: string;
    public thirdQuarter: string;

    //#endregion

    //#region Ctor

    public constructor(opObj: any = null) {
        if (opObj instanceof OpDeviceOrderNumber) {
            this.copy(<OpDeviceOrderNumber>opObj, true, true, true, true)
        } else {
            this.fromJSON(opObj)
        }
    }

    //#endregion

    //#region ICopyable Implementation

    public copy(source: OpDeviceOrderNumber, includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): void {
        if (source == null) {
            return
        }

        if (includeDataList && source.dataList != null) {
            this.dataList = source.dataList.clone(includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)
        }

        this.idDeviceOrderNumber = source.idDeviceOrderNumber;
        this.name = source.name;
        this.firstQuarter = source.firstQuarter;
        this.secondQuarter = source.secondQuarter;
        this.thirdQuarter = source.thirdQuarter;

        this.opState = source.opState;
    }

    public clone(includeNavigationProperties: boolean = false, includeCustomProperties: boolean = false, includeDataList: boolean = false, includeDynamicValues: boolean = false): OpDeviceOrderNumber {
        const newDeviceOrderNumber = new OpDeviceOrderNumber()

        newDeviceOrderNumber.copy(this, includeNavigationProperties, includeCustomProperties, includeDataList, includeDynamicValues)

        return newDeviceOrderNumber
    }

    //#endregion

    //#region IJsonSerializable Implementation

    public fromJSON(opObj: any, compressed: boolean = false): void {
        if (opObj != null) {
            if (opObj.DataList) {
                this.dataList.push(...opObj.DataList.map(data => new OpDeviceOrderNumberData(data)))
            }

            this.idDeviceOrderNumber = opObj.ID_DEVICE_ORDER_NUMBER;
            this.firstQuarter = opObj.FIRST_QUARTER;
            this.secondQuarter = opObj.SECOND_QUARTER;
            this.thirdQuarter = opObj.THIRD_QUARTER;
            this.name = opObj.NAME;
            this.opState = opObj.OpState;
        }
    }

    public toJSON(compressed: boolean = false): any {
        return {
            ID_DEVICE_ORDER_NUMBER: this.idDeviceOrderNumber,
            FIRST_QUARTER: this.firstQuarter,
            SECOND_QUARTER: this.secondQuarter,
            THIRD_QUARTER: this.thirdQuarter,
            NAME: this.name,
            OpState: this.opState,
            DataList: this.dataList != null ? this.dataList.ToJSON() : null,
        }
    }

    //#endregion

    //#region IOpChangeState Implementation

    public opState: Enums.OpChangeState;

    //#endregion

    //#region IOpObject Implementation

    public get idObject(): any {
        return this.idDeviceOrderNumber
    }

    public set idObject(value: any) {
        this.idDeviceOrderNumber = value
    }

    public dataList: OpDataList<OpDeviceOrderNumberData> = new OpDataList<OpDeviceOrderNumberData>()

    public dynamicValues: { Key: string, Value: any }[] = []

    public customFormatString: string


    //#endregion

    //#region IReferenceType Implementation

    public getReferenceType(): Enums.ReferenceType {
        return Enums.ReferenceType.IdDeviceOrderNumber;
    }

    public getReferenceKey(): number {
        return this.idDeviceOrderNumber;
    }

    public getReferenceValue() {
        return this;
    }

    //#endregion

    //#region toString

    public toString(): string {
        if (TypeHelper.isNullOrEmpty(this.firstQuarter) && TypeHelper.isNullOrEmpty(this.secondQuarter) && TypeHelper.isNullOrEmpty(this.thirdQuarter)) {
            return this.name;
        }

        return `${this.name} ${this.firstQuarter}-${this.secondQuarter}-${this.thirdQuarter}`;
    }

    //#endregion

    //#region fromIOpObject

    public static fromIOpObject(object: IOpObject): OpDeviceOrderNumber {
        const newObject: OpDeviceOrderNumber = new OpDeviceOrderNumber(null)

        newObject.idDeviceOrderNumber = object.idObject
        newObject.opState = object.opState
        newObject.dataList.push(...object.dataList.toArray().map((data: IOpData) => OpDeviceOrderNumberData.fromIOpData(data)))

        return newObject
    }

    //#endregion

    //#region loadNavigationProperties

    public static loadNavigationProperties(dataProvider: DataProvider, list: OpDeviceOrderNumber[]): JQueryPromise<OpDeviceOrderNumber[]> {
        return Utils.loadDataListNavigationProperties(dataProvider, list)
    }

    //#endregion
}